import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    HostListener,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    ViewChild,
    ElementRef
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { UserService } from '@intranet/modules/users';
import { AvailabilityService, UserAvailabilitySummary } from './availability.service';

interface Bubble {
    id: string;
    name: string;
    rawImageUrl: string | null;
    imageUrl: SafeUrl | null;
    bgColor: string;
    textColor: string;
    isHovered?: boolean;
    tooltipDown?: boolean;
    tooltipShiftX?: number;
    tooltipTop?: number;
    tooltipLeft?: number;
    awayDays?: number;
    awayToday?: boolean;
    awayLines?: string[];
    // Photo has finished loading — until then it's kept invisible over the
    // initials (see avatar.component.html).
    imageLoaded?: boolean;
}

// Photo URLs that have already finished loading at least once on this page.
// Shared across every avatar instance, so one created later for the same
// photo (e.g. each time the Owner dropdown opens) shows it straight away
// rather than repeating the initials-then-fade-in of the very first load.
const loadedImageUrls = new Set<string>();

@Component({
    selector: 'my-avatar',
    templateUrl: './avatar.component.html',
    styleUrls: ['./avatar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvatarComponent implements OnInit, OnChanges, OnDestroy {
    @Input() size: 'xsmall' | 'small' | 'medium' | 'large' = 'small';

    // single avatar
    @Input() name = '';
    @Input() labelName?: string;
    @Input() firstNameOnly = false;
    @Input() userId?: string;
    @Input() imageUrl?: string;
    @Input() showLabel = false;
    @Input() labelPosition: 'right' | 'bottom' = 'right';

    // group avatar
    @Input() multi = false;
    @Input() users: { id: string; name: string; imageUrl?: string }[] = [];
    @Input() teamNames: string[] = [];
    @Input() maxShown = 2;

    // tooltip
    @Input() showTooltip = true;
    @Input() showNameAbove = true;
    @Input() tooltipPlacement: 'top' | 'bottom' | 'left' | 'right' = 'bottom';
    @Input() tooltipShowCaret = true;
    // Opt-in: positions the tooltip against the viewport (position: fixed)
    // instead of the avatar, so it can spill outside a scrolling ancestor
    // (e.g. a table wrapper with overflow-x: auto, which also clips
    // vertically) rather than being cut off or adding a scrollbar to it.
    @Input() tooltipFixed = false;
    // Opt-in, tooltipFixed only: open below the avatar whenever it fits
    // there, instead of the usual above-first (e.g. a one-row table, where
    // above would run over the header and tabs).
    @Input() tooltipPreferDown = false;
    // Opt-in: the "+N" list opens leftwards, its right edge lined up with
    // the avatars' right edge, instead of rightwards from their left edge
    // (e.g. the Planner's side panel, right by the screen's edge).
    @Input() extraDropdownOpensLeft = false;
    @Output() bubbleClick = new EventEmitter<void>();
    @Output() dropdownOpen = new EventEmitter<boolean>();
    @Input() isOpen = false;
    @Input() enableServiceFallback = true;
    @Input() forceFallbackInPreview = false;

    // Opt-in only — off by default, so no existing usage of <my-avatar> is
    // affected unless it explicitly turns this on.
    @Input() showAvailability = false;
    public awayDays = 0;
    public awayToday = false;
    public awayLines: string[] = [];
    private availabilityMap: Map<string, UserAvailabilitySummary> | null = null;
    private availabilitySub?: Subscription;

    public resolvedImageUrl: SafeUrl | null = null;
    public resolvedUserImages: Bubble[] = [];
    public extraAvatars: Bubble[] = [];
    public showExtraMenu = false;
    public isHovered = false;
    public isSingleHovered: boolean = false;
    public singleBgColor: string;
    public singleTextColor: string;
    public dropdownPositioned = false;
    public singleTooltipDown = false;
    public singleTooltipShiftX = 0;
    public singleTooltipTop = 0;
    public singleTooltipLeft = 0;
    public singleTooltipPositioned = false;
    hasImageError = false;
    // Same as Bubble.imageLoaded, for the single avatar.
    imageLoaded = false;
    private resolvedRawImageUrl: string | null = null;

    @ViewChild('extraDropdown') extraDropdownRef?: ElementRef<HTMLElement>;
    @ViewChild('extraTrigger') extraTriggerRef?: ElementRef<HTMLElement>;

    private originalDropdownParent: Node | null = null;

    constructor(
        private userService: UserService,
        private sanitizer: DomSanitizer,
        private cdr: ChangeDetectorRef,
        private availabilityService: AvailabilityService
    ) {}

    ngOnInit() {
        this.buildAvatars();
        this.computeSingleColors();
        if (!this.multi) {
            this.setSingleImage(this.imageUrl
                || (this.userId && this.userService.getAvatarUrl(this.userId))
                || null);
            if (!this.name && this.userId) {
                const a = this.userService.getAvatarsArray().find(x => x.userId === this.userId);
                if (a?.fullName) this.name = a.fullName;
                this.computeSingleColors();
            }
        }
        if (this.showAvailability) {
            this.availabilitySub = this.availabilityService.getSummaries().subscribe(map => {
                this.availabilityMap = map;
                this.applyAvailability();
                this.cdr.markForCheck();
            });
        }
    }

    private computeSingleColors() {
        this.singleBgColor   = this.getBackgroundColor(this.name);
        this.singleTextColor = this.getTextColor(this.name);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.name && !this.multi) {
            this.computeSingleColors();
        }

        if (!this.multi && (changes.userId || changes.imageUrl)) {
            this.setSingleImage(this.imageUrl
                || (this.userId && this.userService.getAvatarUrl(this.userId))
                || null);
            if (!this.name && this.userId) {
                const a = this.userService.getAvatarsArray().find(x => x.userId === this.userId);
                if (a?.fullName) this.name = a.fullName;
            }
            this.computeSingleColors();
        }
        if (this.multi && (changes.users || changes.teamNames)) {
            this.buildAvatars();
        }
        if (!this.multi && changes.userId) {
            this.applyAvailability();
        }

        if (changes.isOpen) {
            if (this.isOpen) {
                this.dropdownPositioned = false;
                setTimeout(() => {
                    this.positionExtraDropdown();
                    this.dropdownPositioned = true;
                    this.cdr.markForCheck();
                });
                this.attachScrollListener();
            } else {
                // Return the dropdown to its original parent before *ngIf
                // tears it down, otherwise Angular's removeChild fails.
                this.restoreDropdownParent();
                this.dropdownPositioned = false;
                this.detachScrollListener();
            }
        }
    }

    ngOnDestroy() {
        this.detachScrollListener();
        this.restoreDropdownParent();
        this.availabilitySub?.unsubscribe();
    }

    private restoreDropdownParent() {
        const dropdownEl = this.extraDropdownRef?.nativeElement;
        if (dropdownEl && this.originalDropdownParent
                && dropdownEl.parentNode === document.body) {
            this.originalDropdownParent.appendChild(dropdownEl);
        }
        this.originalDropdownParent = null;
    }

    private scrollHandler = (event: Event) => {
        if (!this.isOpen) return;
        // Ignore scrolls originating inside the dropdown itself (the user
        // scrolling its list of users) — only close on outer scrolls.
        const dropdownEl = this.extraDropdownRef?.nativeElement;
        const target = event.target as Node | null;
        if (dropdownEl && target && dropdownEl.contains(target)) return;
        this.dropdownOpen.emit(false);
    };

    private attachScrollListener() {
        // Capture phase so we catch scrolls on any ancestor container,
        // not just the window. Scroll events do not bubble.
        document.addEventListener('scroll', this.scrollHandler, true);
    }

    private detachScrollListener() {
        document.removeEventListener('scroll', this.scrollHandler, true);
    }

    private positionExtraDropdown() {
        const dropdownEl = this.extraDropdownRef?.nativeElement;
        const triggerEl = this.extraTriggerRef?.nativeElement;
        if (!dropdownEl || !triggerEl) return;

        // Detach to <body> so the dropdown isn't trapped under any ancestor
        // stacking context (e.g., the page's sticky search bar with its own
        // z-index). Restored on close.
        if (dropdownEl.parentNode !== document.body) {
            this.originalDropdownParent = dropdownEl.parentNode;
            document.body.appendChild(dropdownEl);
        }

        const triggerRect = triggerEl.getBoundingClientRect();
        const dropdownHeight = dropdownEl.offsetHeight;

        const spaceBelow = window.innerHeight - triggerRect.bottom;
        const spaceAbove = triggerRect.top;

        const shouldOpenUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

        const margin = 8;
        const available = (shouldOpenUpward ? spaceAbove : spaceBelow) - margin;
        const cap = Math.max(Math.min(available, 300), 80);
        dropdownEl.style.maxHeight = `${cap}px`;

        // Anchor to the avatar group's left edge so the dropdown lines up with
        // the bubbles, not with the +N circle alone — or, opening leftwards
        // (extraDropdownOpensLeft), its right edge to the group's right edge.
        // Near the screen's right edge, moved back left just enough to stay
        // on screen rather than running off it.
        const groupEl = triggerEl.parentElement;
        const groupRect = groupEl ? groupEl.getBoundingClientRect() : triggerRect;
        const anchorLeft = this.extraDropdownOpensLeft
            ? groupRect.right - dropdownEl.offsetWidth
            : groupRect.left;
        const maxLeft = window.innerWidth - dropdownEl.offsetWidth - margin;
        const left = Math.max(margin, Math.min(anchorLeft, maxLeft));
        dropdownEl.style.left = `${left}px`;

        if (shouldOpenUpward) {
            dropdownEl.style.top = 'auto';
            dropdownEl.style.bottom = `${window.innerHeight - triggerRect.top}px`;
        } else {
            dropdownEl.style.top = `${triggerRect.bottom}px`;
            dropdownEl.style.bottom = 'auto';
        }
    }

    private buildAvatars() {
        // map original users/teams to Bubbles with placeholders
        const all: Bubble[] = [
            ...this.users.map(u => ({
                id: u.id,
                name: u.name,
                rawImageUrl: u.imageUrl
                  ?? (( this.forceFallbackInPreview || this.enableServiceFallback )
                      ? this.userService.getAvatarUrl(u.id)
                      : null),
                imageUrl: null,
                bgColor: '',
                textColor: '',
                isHovered: false,
            })),
            ...this.teamNames.map((n, i) => ({
                id: `team-${i}`,
                name: n,
                rawImageUrl: null,
                imageUrl: null,
                bgColor: '',
                textColor: '',
                isHovered: false,
            }))
        ];

        // A rebuild for the same person keeps the same <img> (trackById),
        // which won't fire (load) again for an unchanged src — carry over
        // whether it had already loaded, or it'd stay hidden for good.
        const previous = [...this.resolvedUserImages, ...this.extraAvatars];

        // ONE-TIME computation
        all.forEach(b => {
            const prev = previous.find(p => p.id === b.id);
            b.imageLoaded = (!!prev?.imageLoaded && prev.rawImageUrl === b.rawImageUrl)
                || (!!b.rawImageUrl && loadedImageUrls.has(b.rawImageUrl));
            b.imageUrl  = this.sanitizeUrl(b.rawImageUrl);
            b.bgColor   = this.getBackgroundColor(b.name);
            b.textColor = this.getTextColor(b.name);
        });

        this.resolvedUserImages = all.slice(0, this.maxShown);
        this.extraAvatars       = all.slice(this.maxShown);
        this.applyAvailability();
    }

    // Looks up each bubble's (or the single avatar's) availability from the
    // already-fetched map — a no-op until showAvailability is on and the map
    // has arrived. Safe to call any time inputs or the map itself change.
    private applyAvailability() {
        if (!this.showAvailability || !this.availabilityMap) return;
        if (this.multi) {
            [...this.resolvedUserImages, ...this.extraAvatars].forEach(b => {
                const summary = this.availabilityMap!.get(b.id);
                b.awayDays = summary ? summary.awayDays : 0;
                b.awayToday = summary ? summary.awayToday : false;
                b.awayLines = summary ? summary.awayLines : [];
            });
        } else if (this.userId) {
            const summary = this.availabilityMap.get(this.userId);
            this.awayDays = summary ? summary.awayDays : 0;
            this.awayToday = summary ? summary.awayToday : false;
            this.awayLines = summary ? summary.awayLines : [];
        }
    }

    get extraCount(): number {
        return this.extraAvatars.length;
    }

    // Whether anyone hidden behind the "+N" bubble has any availability to
    // flag at all — otherwise a stacked absence could disappear entirely
    // behind the overflow count with no hint it's there.
    get extraAvatarsHaveAway(): boolean {
        return this.extraAvatars.some(b => (b.awayDays || 0) > 0);
    }

    // Whether any of them are away specifically today, for the same red-vs-
    // blue distinction a single avatar's own badge makes.
    get extraAvatarsAwayToday(): boolean {
        return this.extraAvatars.some(b => b.awayToday);
    }

    // Whether an "Away:" line is today's (optionally with a half-day
    // suffix, e.g. "Today am") — used to colour just that line the same
    // red as the badge's away-today state.
    isTodayLine(day: string): boolean {
        return day.startsWith('Today');
    }

    onImageErrorFor(b: Bubble) {
        b.imageUrl = null;
        b.rawImageUrl = null;
        b.bgColor = this.getBackgroundColor(b.name);
        b.textColor = this.getTextColor(b.name);
        this.cdr.detectChanges();
    }

    trackById(_: number, b: Bubble) {
        return b.id;
    }

    toggleExtraMenu() {
        this.showExtraMenu = !this.showExtraMenu;
        this.dropdownOpen.emit(!this.isOpen);
    }
    @HostListener('document:click')
    onDocumentClick() {
        this.dropdownOpen.emit(false); // tell parent to close
    }

    @HostListener('window:resize')
    onWindowResize() {
        // The dropdown is position:fixed, so it doesn't follow the trigger when
        // the viewport resizes. Close it instead.
        if (this.isOpen) {
            this.dropdownOpen.emit(false);
        }
    }

    // Only a genuinely different photo goes back to showing the initials
    // until it loads — re-setting the same one wouldn't fire (load) again.
    private setSingleImage(raw: string | null) {
        if (raw !== this.resolvedRawImageUrl) {
            this.resolvedRawImageUrl = raw;
            this.resolvedImageUrl = this.sanitizeUrl(raw);
            this.imageLoaded = !!raw && loadedImageUrls.has(raw);
        }
    }

    onImageLoad(): void {
        this.imageLoaded = true;
        if (this.resolvedRawImageUrl) loadedImageUrls.add(this.resolvedRawImageUrl);
    }

    onImageLoadFor(b: Bubble): void {
        b.imageLoaded = true;
        if (b.rawImageUrl) loadedImageUrls.add(b.rawImageUrl);
    }

    public onImageError(): void {
        this.resolvedImageUrl = null;
        this.cdr.markForCheck();
    }

    private sanitizeUrl(raw: string | null): SafeUrl | null {
        return raw ? this.sanitizer.bypassSecurityTrustUrl(raw) : null;
    }

    fallbackFor(id: string): SafeUrl | null {
        const raw = this.userService.getAvatarUrl(id);
        return this.sanitizeUrl(raw);
    }

    getInitials(name: string): string {
      const parts = name.trim().split(/\s+/);
      if (parts.length === 1) {
        // single word: use first two letters
        return parts[0].substring(0, 2).toUpperCase();
      }
      // two+ words: first letter of word1 + first of word2
      return (parts[0][0] || '').toUpperCase() +
             (parts[1][0] || '').toUpperCase();
    }

    get labelToShow(): string {
        // if labelName is provided, use it
        if (this.labelName != null) {
            return this.labelName;
        }
        //  if name is empty, return empty string
        if (!this.name) return '';
        if (this.firstNameOnly) {
            return this.name.split(/\s+/)[0];
        }
        return this.name;
    }

    private hashString(str: string): number {
        let h = 0; for (let c of str) h = c.charCodeAt(0) + ((h << 5) - h);
        return Math.abs(h);
    }

    private hslToRgb(h: number, s: number, l: number) {
        s /= 100; l /= 100;
        const k = (n: number) => (n + h / 30) % 12;
        const a = s * Math.min(l, 1 - l);
        const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
        return { r: 255 * f(0), g: 255 * f(8), b: 255 * f(4) };
    }

    getTextColor(name: string): string {
        const hash = this.hashString(name);
        const h = hash * 137.508 % 360;
        const s = 50 + hash % 30;
        const l = 55 + ((hash >> 5) % 20);
        const { r, g, b } = this.hslToRgb(h, s, l);
        return (r * 299 + g * 587 + b * 114) / 1000 > 128 ? 'rgba(0,0,0,0.87)' : '#fff';
    }

    getBackgroundColor(name: string): string {
        const hash = this.hashString(name);
        const h = hash * 137.508 % 360;
        const s = 50 + hash % 30;
        const l = 55 + ((hash >> 5) % 20);
        return `hsl(${h.toFixed(1)},${s.toFixed(1)}%,${l.toFixed(1)}%)`;
    }

    onAvatarGroupClick(event: MouseEvent) {
        this.bubbleClick.emit();
    }

    onBubbleClick(event: MouseEvent) {
        this.bubbleClick.emit();
    }

    @HostListener('click')
    handleClick() {
        this.bubbleClick.emit();
    }

    onExtraAvatarClick(b: Bubble) {
        this.showExtraMenu = false;
        this.bubbleClick.emit();
    }

    onBubbleEnter(b: Bubble, event: MouseEvent) {
        const wrapperEl = event.currentTarget as HTMLElement;
        if (this.tooltipFixed) {
            const pos = this.computeFixedTooltipPosition(wrapperEl);
            b.tooltipTop = pos.top;
            b.tooltipLeft = pos.left;
            return;
        }
        // Every bubble in the same stack sits in the same row, so they must
        // all agree on a direction — sizing the check against the tallest
        // tooltip in the group (not just this bubble's own, possibly
        // shorter, one) stops a short "just a name" tooltip from opening up
        // right next to a taller "name + away list" neighbour opening down.
        b.tooltipDown = this.shouldTooltipFlipDown(wrapperEl, this.tallestTooltipHeightInGroup(wrapperEl));
        // The group tooltip is always in the DOM (shown via CSS opacity on
        // hover), so this can be measured immediately.
        b.tooltipShiftX = this.computeTooltipShiftX(wrapperEl);
    }

    onSingleEnter(event: MouseEvent) {
        this.isSingleHovered = true;
        const wrapperEl = event.currentTarget as HTMLElement;
        if (this.tooltipFixed) {
            // Hidden until measured, so it doesn't flash at a stale spot
            // for a frame before it's been placed.
            this.singleTooltipPositioned = false;
            setTimeout(() => {
                const pos = this.computeFixedTooltipPosition(wrapperEl);
                this.singleTooltipTop = pos.top;
                this.singleTooltipLeft = pos.left;
                this.singleTooltipPositioned = true;
                this.cdr.markForCheck();
            });
            return;
        }
        // Best guess before the tooltip exists in the DOM, so it doesn't
        // flash open in the wrong direction on the first frame.
        this.singleTooltipDown = this.shouldTooltipFlipDown(wrapperEl);
        // Unlike the group tooltip, the single tooltip only enters the DOM
        // once Angular processes isSingleHovered above — defer to the next
        // tick so both the shift and the flip direction are measured against
        // its real height, not the hardcoded fallback estimate (which is too
        // short for multi-line "away" tooltips and can leave it clipped
        // above a sticky table header).
        setTimeout(() => {
            this.singleTooltipDown = this.shouldTooltipFlipDown(wrapperEl);
            this.singleTooltipShiftX = this.computeTooltipShiftX(wrapperEl);
            this.cdr.markForCheck();
        });
    }

    // The tallest tooltip among this bubble's stack-mates, so the whole
    // group can be checked against one shared height. Returns 0 outside a
    // group (or if nothing's found yet), which just falls back to this
    // bubble's own tooltip height in shouldTooltipFlipDown.
    private tallestTooltipHeightInGroup(wrapperEl: HTMLElement): number {
        const group = wrapperEl.closest('.Avatar-group');
        if (!group) return 0;
        let max = 0;
        group.querySelectorAll('.Avatar-tooltip-container').forEach(el => {
            max = Math.max(max, (el as HTMLElement).offsetHeight);
        });
        return max;
    }

    // The tooltip opens upward by default; this only returns true when
    // there isn't enough room above to fit it, in which case it drops down
    // instead.
    private shouldTooltipFlipDown(wrapperEl: HTMLElement, knownHeight?: number): boolean {
        const rect = wrapperEl.getBoundingClientRect();
        const tooltipEl = wrapperEl.querySelector(
            '.Avatar-tooltip-container'
        ) as HTMLElement | null;
        // Estimate the tooltip height when it isn't laid out yet (single
        // avatar uses *ngIf so it isn't in the DOM until hover).
        const tooltipHeight = knownHeight || tooltipEl?.offsetHeight || 28;

        let clipTop = 0;
        let clipBottom = window.innerHeight;
        let hasScrolled = window.scrollY > 0 || document.documentElement.scrollTop > 0;
        let parent: HTMLElement | null = wrapperEl.parentElement;
        while (parent && parent !== document.body) {
            const style = getComputedStyle(parent);
            const clipsY = ['hidden', 'auto', 'scroll', 'clip'].includes(style.overflowY)
                || ['hidden', 'auto', 'scroll', 'clip'].includes(style.overflow);
            if (clipsY) {
                const r = parent.getBoundingClientRect();
                clipTop = Math.max(clipTop, r.top);
                clipBottom = Math.min(clipBottom, r.bottom);
            }
            if (parent.scrollTop > 0) hasScrolled = true;
            parent = parent.parentElement;
        }
        clipTop = Math.max(clipTop, this.coveringTableHeadBottom(wrapperEl));

        const spaceAbove = rect.top - clipTop;
        if (spaceAbove < tooltipHeight + 8) {
            // Only drop down if that actually gives it more room - in a
            // short table (one or two rows inside a scrolling wrapper)
            // there may be even less space below than above.
            const spaceBelow = clipBottom - rect.bottom;
            return spaceBelow > spaceAbove;
        }

        // A sticky element always reports position:sticky, whether or not
        // it's actually detached and floating over anything right now —
        // that only happens once something has scrolled past it. At rest,
        // it's just sitting in normal flow like anything else, so there's
        // nothing to check for yet.
        if (!hasScrolled) return false;

        return this.isCoveredBySticky(wrapperEl, rect, tooltipHeight);
    }

    // Viewport coordinates for a tooltipFixed tooltip. Scrolling/overflow
    // ancestors no longer clip it, so only the viewport and anything that
    // still paints over it (a sticky header stacked above the row, or a
    // pinned bar) limit where it can go. Opens upward when it fits there,
    // otherwise below; always kept inside the viewport horizontally.
    private computeFixedTooltipPosition(wrapperEl: HTMLElement): { top: number; left: number } {
        const tooltipEl = wrapperEl.querySelector('.Avatar-tooltip-container') as HTMLElement | null;
        const rect = wrapperEl.getBoundingClientRect();
        const width = tooltipEl?.offsetWidth || 0;
        const height = tooltipEl?.offsetHeight || 28;
        const gap = 3;
        const margin = 8;

        const topLimit = Math.max(0, this.coveringTableHeadBottom(wrapperEl));
        const spaceAbove = rect.top - topLimit;
        const spaceBelow = window.innerHeight - rect.bottom;
        const fitsAbove = spaceAbove >= height + gap + margin
            && !this.isCoveredBySticky(wrapperEl, rect, height);
        const fitsBelow = spaceBelow >= height + gap + margin;
        const openUp = this.tooltipPreferDown && fitsBelow
            ? false
            : fitsAbove || (!fitsBelow && spaceAbove > spaceBelow);

        const top = openUp ? rect.top - height - gap : rect.bottom + gap;
        const centred = rect.left + rect.width / 2 - width / 2;
        const left = Math.max(margin, Math.min(centred, window.innerWidth - width - margin));
        return { top, left };
    }

    // Some tables (e.g. the action snapshot report) stack their sticky
    // header above even a hovered row, so anything the row draws upward -
    // this tooltip included - slides underneath the header, even at rest
    // with nothing scrolled. Returns the header's bottom edge in that case,
    // so it's treated as the top limit, or 0 when the header sits below the
    // row (the usual case, where the tooltip already paints over it fine).
    private coveringTableHeadBottom(wrapperEl: HTMLElement): number {
        const row = wrapperEl.closest('tr');
        const head = row?.closest('table')?.querySelector('thead');
        if (!row || !head || head.contains(row)) return 0;

        const headStyle = getComputedStyle(head);
        if (headStyle.position !== 'sticky' && headStyle.position !== 'fixed') return 0;

        const headZ = parseInt(headStyle.zIndex, 10);
        const rowZ = parseInt(getComputedStyle(row).zIndex, 10);
        if (isNaN(headZ) || (!isNaN(rowZ) && headZ <= rowZ)) return 0;

        return head.getBoundingClientRect().bottom;
    }

    // A sticky (or fixed) filter bar doesn't clip via overflow, so the walk
    // above never sees it — it just gets painted on top of the scrolled
    // content beneath it, including our tooltip, no matter how high its
    // z-index is (it's a different stacking context, one z-index can't
    // reach into). Sample the point the tooltip would actually occupy and
    // ask the browser what's really there. Ordinary in-flow content (e.g.
    // the row above) is ignored — the tooltip already renders above that
    // fine — only something pinned counts as a real obstruction.
    private isCoveredBySticky(wrapperEl: HTMLElement, rect: DOMRect, tooltipHeight: number): boolean {
        const sampleX = rect.left + rect.width / 2;
        const sampleY = rect.top - tooltipHeight - 4;
        if (sampleY < 0 || sampleX < 0 || sampleX > window.innerWidth) return false;

        const elementThere = document.elementFromPoint(sampleX, sampleY);
        if (!elementThere || wrapperEl.contains(elementThere)) return false;

        let el: HTMLElement | null = elementThere as HTMLElement;
        while (el && el !== document.body) {
            const position = getComputedStyle(el).position;
            if (position === 'sticky' || position === 'fixed') return true;
            el = el.parentElement;
        }
        return false;
    }

    // Tooltip is centred under the avatar by default; nudge it sideways
    // (via the --tooltip-shift CSS variable) whenever that would run it past
    // the nearest edge that actually clips it — either the viewport, or an
    // ancestor with overflow:hidden/auto/scroll (e.g. a table), whichever is
    // tighter. Shifting alone can't help once the tooltip is wider than that
    // ancestor allows, which is what the max-width in the scss is for.
    private computeTooltipShiftX(wrapperEl: HTMLElement): number {
        const tooltipEl = wrapperEl.querySelector('.Avatar-tooltip-container') as HTMLElement | null;
        if (!tooltipEl) return 0;

        const margin = 8;
        const rect = wrapperEl.getBoundingClientRect();
        const tooltipWidth = tooltipEl.offsetWidth;
        const centerX = rect.left + rect.width / 2;

        let clipLeft = 0;
        let clipRight = window.innerWidth;
        let parent: HTMLElement | null = wrapperEl.parentElement;
        while (parent && parent !== document.body) {
            const style = getComputedStyle(parent);
            const clipsX = ['hidden', 'auto', 'scroll', 'clip'].includes(style.overflowX)
                || ['hidden', 'auto', 'scroll', 'clip'].includes(style.overflow);
            if (clipsX) {
                const r = parent.getBoundingClientRect();
                clipLeft = Math.max(clipLeft, r.left);
                clipRight = Math.min(clipRight, r.right);
            }
            parent = parent.parentElement;
        }

        const leftEdge = centerX - tooltipWidth / 2;
        const rightEdge = centerX + tooltipWidth / 2;

        if (leftEdge < clipLeft + margin) {
            return (clipLeft + margin) - leftEdge;
        }
        if (rightEdge > clipRight - margin) {
            return (clipRight - margin) - rightEdge;
        }
        return 0;
    }
}
